import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Briefcase, User, LogOut, Settings, MapPin, Clock, Crown, AlertCircle, MessageCircle, X, ChevronRight, Bell } from 'lucide-react';
import SupportButton from '../components/SupportButton';
import RoleSwitcher from '../components/RoleSwitcher';
import { Alert, AlertDescription } from '../components/ui/alert';
import Notifications from '../components/Notifications';
import PromotionalBanner from '../components/PromotionalBanner';
import BannerSidebar from '../components/BannerSidebar';
import BannerMobileStrip from '../components/BannerMobileStrip';
import { ProfessionalPaymentGate } from '../components/ProfessionalPaymentGate';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { SERVICE_CATEGORIES } from '../constants/categories';

const ProfessionalDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, API, language } = useContext(AuthContext);
  const [jobs, setJobs] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myJobsLoading, setMyJobsLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [bidDialogOpen, setBidDialogOpen] = useState(false);
  const [selectedJobForBid, setSelectedJobForBid] = useState(null);
  const [bidAmount, setBidAmount] = useState('');
  const [bidMessage, setBidMessage] = useState('');
  const [submittingBid, setSubmittingBid] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categorySearch, setCategorySearch] = useState('');

  useEffect(() => {
    fetchSubscriptionStatus();
    fetchVerificationStatus();
  }, []);

  useEffect(() => {
    if (verificationStatus?.status === 'approved' && subscriptionStatus?.is_active) {
      fetchJobs();
      fetchMyJobs();
    } else if (verificationStatus && subscriptionStatus) {
      setLoading(false);
    }
  }, [verificationStatus?.status, subscriptionStatus?.is_active]);

  useEffect(() => {
    if (verificationStatus?.status === 'approved' && subscriptionStatus?.is_active) return undefined;
    const timer = window.setInterval(() => {
      fetchVerificationStatus();
      fetchSubscriptionStatus();
    }, 10000);
    return () => window.clearInterval(timer);
  }, [verificationStatus?.status, subscriptionStatus?.is_active]);
  
  const fetchVerificationStatus = async () => {
    try {
      const response = await axios.get(`${API}/users/me/verification-status`);
      setVerificationStatus(response.data);
    } catch (error) {
      console.error('Failed to load verification status');
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/status?role=professional`);
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to load subscription status');
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${API}/jobs?status=open`);
      setJobs(response.data);
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyJobs = async () => {
    setMyJobsLoading(true);
    try {
      const response = await axios.get(`${API}/jobs?assigned_to=me`);
      setMyJobs(response.data);
    } catch (error) {
      console.error('Failed to load my jobs');
    } finally {
      setMyJobsLoading(false);
    }
  };

  const fetchAIMatches = async () => {
    setAiLoading(true);
    try {
      const token = localStorage.getItem('helply_token');
      const response = await axios.post(`${API}/ai/match-jobs`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAiRecommendations(response.data.recommendations);
      toast.success(language === 'en' ? 'AI matches loaded!' : language === 'it' ? 'Suggerimenti IA caricati!' : 'KI-Empfehlungen geladen!');
    } catch (error) {
      toast.error('Failed to load AI recommendations');
    } finally {
      setAiLoading(false);
    }
  };

  const openBidDialog = (job) => {
    const needsVerification = verificationStatus?.status !== 'approved';
    const needsSubscription = !subscriptionStatus?.is_active;
    
    if (needsVerification || needsSubscription) {
      if (needsVerification) {
        toast.error(
          language === 'en' 
            ? 'Please complete identity verification to place bids'
            : language === 'it'
            ? 'Completa la verifica dell\'identità per fare offerte'
            : language === 'fr'
            ? 'Veuillez compléter la vérification d\'identité pour faire des offres'
            : 'Bitte vervollständigen Sie die Identitätsprüfung, um Angebote abzugeben'
        );
        navigate('/identity-verification');
      } else if (needsSubscription) {
        toast.error(
          language === 'en' 
            ? 'Please complete subscription to place bids'
            : language === 'it'
            ? 'Completa l\'abbonamento per fare offerte'
            : language === 'fr'
            ? 'Veuillez compléter l\'abonnement pour faire des offres'
            : 'Bitte vervollständigen Sie das Abonnement, um Angebote abzugeben'
        );
        navigate('/subscription-payment?billing_role=professional');
      }
      return;
    }
    
    setSelectedJobForBid(job);
    setBidAmount(job.budget.toString());
    setBidMessage('');
    setBidDialogOpen(true);
  };

  const closeBidDialog = () => {
    setBidDialogOpen(false);
    setSelectedJobForBid(null);
    setBidAmount('');
    setBidMessage('');
  };

  const placeBid = async () => {
    if (!bidAmount || parseFloat(bidAmount) <= 0) {
      toast.error(language === 'en' ? 'Please enter a valid bid amount' : language === 'it' ? 'Inserisci un importo valido' : language === 'fr' ? 'Veuillez entrer un montant valide' : 'Bitte geben Sie einen gültigen Angebotsbetrag ein');
      return;
    }

    setSubmittingBid(true);
    try {
      await axios.post(`${API}/bids`, {
        job_id: selectedJobForBid.id,
        proposed_price: parseFloat(bidAmount),
        message: bidMessage
      });
      toast.success(language === 'en' ? 'Bid placed successfully!' : language === 'it' ? 'Offerta inviata con successo!' : language === 'fr' ? 'Offre soumise avec succès!' : 'Angebot erfolgreich abgegeben!');
      closeBidDialog();
      fetchJobs();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' ? errorDetail :
        (typeof errorDetail === 'object' && errorDetail?.message) ? errorDetail.message :
        (language === 'en' ? 'Failed to place bid' : language === 'it' ? 'Impossibile inviare l\'offerta' : language === 'fr' ? 'Impossible de soumettre l\'offre' : 'Angebot konnte nicht abgegeben werden');
      toast.error(errorMessage);
      if (error.response?.status === 402) {
        navigate('/subscription-payment?billing_role=professional');
      }
    } finally {
      setSubmittingBid(false);
    }
  };

  const content = {
    en: {
      title: 'Job Marketplace',
      allJobs: 'All Jobs',
      myJobs: 'My Jobs',
      aiMatches: 'AI Matches',
      getAIMatches: 'Get AI Matches',
      placeBid: 'Place Bid',
      viewDetails: 'Details',
      openChat: 'Open Chat',
      budget: 'Budget',
      urgency: 'Urgency',
      noJobs: 'No open jobs available',
      noMyJobs: 'No assigned jobs yet',
      assignedJobs: 'Jobs where your bid was accepted'
    },
    de: {
      title: 'Auftragsmarkt',
      allJobs: 'Alle Aufträge',
      myJobs: 'Meine Aufträge',
      aiMatches: 'KI-Empfehlungen',
      getAIMatches: 'KI-Empfehlungen abrufen',
      placeBid: 'Angebot abgeben',
      viewDetails: 'Details',
      openChat: 'Chat öffnen',
      budget: 'Budget',
      urgency: 'Dringlichkeit',
      noJobs: 'Keine offenen Aufträge verfügbar',
      noMyJobs: 'Noch keine zugewiesenen Aufträge',
      assignedJobs: 'Aufträge, bei denen Ihr Angebot angenommen wurde'
    },
    it: {
      title: 'Mercato Lavori',
      allJobs: 'Tutti i Lavori',
      myJobs: 'I Miei Lavori',
      aiMatches: 'Suggerimenti IA',
      getAIMatches: 'Ottieni Suggerimenti IA',
      placeBid: 'Fai un\'offerta',
      viewDetails: 'Dettagli',
      openChat: 'Apri Chat',
      budget: 'Budget',
      urgency: 'Urgenza',
      noJobs: 'Nessun lavoro disponibile',
      noMyJobs: 'Nessun lavoro assegnato ancora',
      assignedJobs: 'Lavori dove la tua offerta è stata accettata'
    },
    fr: {
      title: 'Marché des Emplois',
      allJobs: 'Tous les Emplois',
      myJobs: 'Mes Emplois',
      aiMatches: 'Suggestions IA',
      getAIMatches: 'Obtenir Suggestions IA',
      placeBid: 'Faire une offre',
      viewDetails: 'Détails',
      openChat: 'Ouvrir le Chat',
      budget: 'Budget',
      urgency: 'Urgence',
      noJobs: 'Aucun emploi disponible',
      noMyJobs: 'Aucun emploi attribué encore',
      assignedJobs: 'Emplois où votre offre a été acceptée'
    }
  };

  const t = content[language] || content.en;
  const needsSubscription = !subscriptionStatus?.is_active;

  const refreshProfessionalAccess = () => {
    fetchVerificationStatus();
    fetchSubscriptionStatus();
  };

  if (!verificationStatus || !subscriptionStatus) {
    return <ProfessionalPaymentGate user={user} language={language} navigate={navigate} logout={logout} onRefresh={refreshProfessionalAccess} loading />;
  }

  if (verificationStatus.status === 'approved' && needsSubscription) {
    return <ProfessionalPaymentGate user={user} language={language} navigate={navigate} logout={logout} onRefresh={refreshProfessionalAccess} />;
  }

  // Helper function to translate category
  const getCategoryName = (categoryValue) => {
    const category = SERVICE_CATEGORIES.find(c => c.value === categoryValue);
    if (!category) return categoryValue;
    return language === 'en' ? category.en : language === 'it' ? category.it : language === 'fr' ? category.fr : category.de;
  };

  // Helper function to translate urgency
  const getUrgencyLabel = (urgency) => {
    const urgencyMap = {
      en: { low: 'Low', medium: 'Medium', high: 'High', emergency: 'Emergency' },
      de: { low: 'Niedrig', medium: 'Mittel', high: 'Hoch', emergency: 'Notfall' },
      it: { low: 'Bassa', medium: 'Media', high: 'Alta', emergency: 'Emergenza' },
      fr: { low: 'Faible', medium: 'Moyen', high: 'Élevé', emergency: 'Urgence' }
    };
    return urgencyMap[language]?.[urgency] || urgencyMap.en[urgency] || urgency;
  };

  const MyJobCard = ({ job }) => (
    <Card className="hover-lift" data-testid={`my-job-card-${job.id}`}>
      <CardHeader>
        <CardTitle className="text-lg font-heading">{job.title}</CardTitle>
        <p className="text-sm text-gray-500">{getCategoryName(job.category)}</p>
        <Badge className="w-fit mt-2 bg-green-100 text-green-800">
          {language === 'en' ? 'Assigned to you' : language === 'it' ? 'Assegnato a te' : language === 'fr' ? 'Assigné à vous' : 'Ihnen zugewiesen'}
        </Badge>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">{job.description}</p>
        
        <div className="space-y-2 text-sm mb-4">
          <div className="flex justify-between">
            <span className="text-gray-500">{t.budget}:</span>
            <span className="font-bold text-blue-600">CHF {job.budget}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">{language === 'en' ? 'Client' : language === 'it' ? 'Cliente' : language === 'fr' ? 'Client' : 'Kunde'}:</span>
            <span className="font-medium">{job.client_name}</span>
          </div>
          {job.location?.address && (
            <div className="flex items-center text-gray-500">
              <MapPin className="w-4 h-4 mr-1" />
              <span className="text-xs">{job.location.address}</span>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <Button 
            size="sm" 
            className="flex-1 bg-blue-600 hover:bg-blue-700"
            onClick={() => navigate(`/chat/${job.id}`)}
            data-testid={`open-chat-btn-${job.id}`}
          >
            {t.openChat}
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => navigate(`/job/${job.id}`)}
            data-testid={`view-my-job-details-btn-${job.id}`}
          >
            {t.viewDetails}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const JobCard = ({ job, isAIMatch = false }) => (
    <Card className="hover-lift relative" data-testid={`job-card-${job.id}`}>
      {isAIMatch && (
        <div className="absolute top-2 right-2">
          <Badge className="bg-purple-100 text-purple-800">
            <Sparkles className="w-3 h-3 mr-1" />
            {language === 'en' ? 'AI Match' : language === 'it' ? 'Match IA' : language === 'fr' ? 'Match IA' : 'KI-Match'}
          </Badge>
        </div>
      )}
      <CardHeader>
        <CardTitle className="text-lg font-heading pr-20">{job.title}</CardTitle>
        <p className="text-sm text-gray-500">{getCategoryName(job.category)}</p>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">{job.description}</p>
        
        <div className="space-y-2 text-sm mb-4">
          <div className="flex justify-between">
            <span className="text-gray-500">{t.budget}:</span>
            <span className="font-bold text-blue-600">CHF {job.budget}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">{t.urgency}:</span>
            <Badge variant="outline">{getUrgencyLabel(job.urgency)}</Badge>
          </div>
          {job.location?.address && (
            <div className="flex items-center text-gray-500">
              <MapPin className="w-4 h-4 mr-1" />
              <span className="text-xs">{job.location.address}</span>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <Button 
            size="sm" 
            className="flex-1 bg-blue-600 hover:bg-blue-700"
            onClick={() => openBidDialog(job)}
            data-testid={`place-bid-btn-${job.id}`}
          >
            {t.placeBid}
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => navigate(`/job/${job.id}`)}
            data-testid={`view-details-btn-${job.id}`}
          >
            {t.viewDetails}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="professional-dashboard">
      {/* Promotional Banner */}
      <PromotionalBanner />
      
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto min-w-0 px-3 sm:px-6 lg:px-8 py-2 flex justify-between items-center">
          <div className="flex items-center">
            <img src="/helply-logo.jpg" alt="Helply" className="w-10 h-10 rounded-lg" />
          </div>
          <div className="flex min-w-0 items-center gap-1 sm:gap-2">
            <RoleSwitcher />
            <Button variant="ghost" size="sm" onClick={() => navigate('/chats')} data-testid="chats-btn" className="relative hidden sm:flex">
              <MessageCircle className="w-5 h-5 text-blue-600" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/notifications')} data-testid="notifications-nav-btn" className="relative hidden sm:flex">
              <Bell className="w-5 h-5 text-gray-600" />
              <span className="ml-1 text-xs text-gray-600 hidden lg:inline">{language === 'en' ? 'Notifications' : language === 'it' ? 'Notifiche' : language === 'fr' ? 'Notifications' : 'Benachrichtigungen'}</span>
            </Button>
            {user?.is_admin && (
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} data-testid="admin-menu-btn" className="text-purple-600">
                <Settings className="w-5 h-5" />
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => navigate('/subscription')} data-testid="subscription-menu-btn" className="hidden sm:flex">
              <Crown className="w-5 h-5 text-blue-600" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/settings')} data-testid="settings-btn">
              <Settings className="w-5 h-5" />
            </Button>
            <div className="hidden items-center gap-1 sm:flex"><SupportButton /><Notifications /></div>
            <div className="hidden sm:flex items-center space-x-2">
              <User className="w-5 h-5 text-gray-600" />
              <span className="text-sm font-medium">{user?.name}</span>
            </div>
            <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">{language === 'en' ? 'Logout' : language === 'it' ? 'Esci' : language === 'fr' ? 'Déconnexion' : 'Abmelden'}</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Subscription Banner */}
        {subscriptionStatus && subscriptionStatus.status === 'trial' && subscriptionStatus.trial_days_remaining <= 7 && (
          <Alert className="mb-6 border-orange-200 bg-orange-50">
            <Crown className="h-5 w-5 text-orange-600" />
            <AlertDescription className="ml-2">
              <div className="flex justify-between items-center">
                <div>
                  <strong className="text-orange-900">
                    {language === 'en' 
                      ? `Your free trial ends in ${subscriptionStatus.trial_days_remaining} days!` 
                      : language === 'it'
                      ? `La tua prova gratuita termina tra ${subscriptionStatus.trial_days_remaining} giorni!`
                      : language === 'fr'
                      ? `Votre essai gratuit se termine dans ${subscriptionStatus.trial_days_remaining} jours !`
                      : `Ihre kostenlose Testversion endet in ${subscriptionStatus.trial_days_remaining} Tagen!`}
                  </strong>
                  <p className="text-sm text-orange-800 mt-1">
                    {language === 'en' 
                      ? 'Subscribe now to continue accessing all features.' 
                      : language === 'it'
                      ? 'Abbonati ora per continuare ad accedere a tutte le funzionalità.'
                      : language === 'fr'
                      ? 'Abonnez-vous maintenant pour continuer à accéder à toutes les fonctionnalités.'
                      : 'Abonnieren Sie jetzt, um weiterhin auf alle Funktionen zuzugreifen.'}
                  </p>
                </div>
                <Button 
                  onClick={() => navigate('/subscription')}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="subscribe-banner-btn"
                >
                  {language === 'en' ? 'Subscribe' : language === 'it' ? 'Abbonati' : language === 'fr' ? 'S\'abonner' : 'Abonnieren'}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}
        
        {/* Onboarding Banner - Shows if user needs verification OR subscription */}
        {verificationStatus && subscriptionStatus && (!verificationStatus.is_verified || !subscriptionStatus.is_active) && (
          <Alert className="mb-6 border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🚀</span>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-blue-900 text-lg mb-1">
                  {language === 'en' ? 'Complete Your Setup to Start Bidding!' : language === 'it' ? 'Completa la configurazione per iniziare a fare offerte!' : language === 'fr' ? 'Complétez votre configuration pour commencer à faire des offres !' : 'Vervollständigen Sie Ihre Einrichtung!'}
                </h3>
                <p className="text-sm text-blue-800 mb-3">
                  {language === 'en' 
                    ? 'Complete these 2 quick steps to unlock all features and start winning jobs!'
                    : language === 'it'
                    ? 'Completa questi 2 rapidi passaggi per sbloccare tutte le funzionalità!'
                    : language === 'fr'
                    ? 'Complétez ces 2 étapes rapides pour débloquer toutes les fonctionnalités !'
                    : 'Vervollständigen Sie diese 2 schnellen Schritte, um alle Funktionen freizuschalten!'}
                </p>
                
                <div className="space-y-2 mb-4">
                  {/* Step 1: Verification */}
                  <div className={`flex items-center gap-2 ${verificationStatus.is_verified ? 'opacity-50' : ''}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      verificationStatus.is_verified ? 'bg-green-600 text-white' : 'bg-blue-600 text-white'
                    }`}>
                      {verificationStatus.is_verified ? '✓' : '1'}
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {language === 'en' ? 'Get Verified' : language === 'it' ? 'Verifica' : language === 'fr' ? 'Vérification' : 'Verifizierung'}
                      <span className="ml-1 text-green-600 font-bold">{language === 'en' ? '(FREE)' : language === 'it' ? '(GRATIS)' : language === 'fr' ? '(GRATUIT)' : '(GRATIS)'}</span>
                    </span>
                    {verificationStatus.is_verified && (
                      <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
                        {language === 'en' ? 'Complete' : language === 'it' ? 'Completato' : language === 'fr' ? 'Terminé' : 'Abgeschlossen'}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Step 2: Subscription */}
                  <div className={`flex items-center gap-2 ${subscriptionStatus.is_active ? 'opacity-50' : ''}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      subscriptionStatus.is_active ? 'bg-green-600 text-white' : 'bg-purple-600 text-white'
                    }`}>
                      {subscriptionStatus.is_active ? '✓' : '2'}
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {language === 'en' ? 'Professional subscription — CHF 99/year' : language === 'it' ? 'Abbonamento professionale — CHF 99/anno' : language === 'fr' ? 'Abonnement professionnel — CHF 99/an' : 'Profi-Abonnement — CHF 99/Jahr'}
                    </span>
                    {subscriptionStatus.is_active && (
                      <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
                        {language === 'en' ? 'Complete' : language === 'it' ? 'Completato' : language === 'fr' ? 'Terminé' : 'Abgeschlossen'}
                      </Badge>
                    )}
                  </div>
                </div>

                {!verificationStatus.is_verified && (
                  <Button 
                    onClick={() => navigate('/identity-verification')}
                    className="bg-blue-600 hover:bg-blue-700"
                    size="sm"
                    data-testid="start-manual-verification-button"
                  >
                    <span className="mr-2">🛡️</span>
                    {language === 'en' ? 'Start Verification' : language === 'it' ? 'Inizia Verifica' : language === 'fr' ? 'Commencer la vérification' : 'Verifizierung starten'}
                  </Button>
                )}
                
                {verificationStatus.is_verified && !subscriptionStatus.is_active && (
                  <Button 
                    onClick={() => navigate('/subscription-payment?billing_role=professional')}
                    className="bg-blue-700 hover:bg-blue-800"
                    size="sm"
                    data-testid="activate-professional-subscription-button"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    {language === 'en' ? 'Activate CHF 99/year' : language === 'it' ? 'Attiva CHF 99/anno' : language === 'fr' ? 'Activer CHF 99/an' : 'CHF 99/Jahr aktivieren'}
                  </Button>
                )}
              </div>
            </div>
          </Alert>
        )}

        {/* Welcome Message - Shows when user has completed ALL setup steps (verification + subscription) */}
        {verificationStatus?.is_verified && subscriptionStatus?.is_active && (
          <Alert className="mb-6 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🎉</span>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-green-900 text-lg mb-1">
                  {language === 'en' 
                    ? 'Welcome to Helply Swiss!' 
                    : language === 'it'
                    ? 'Benvenuto su Helply Swiss!'
                    : language === 'fr'
                    ? 'Bienvenue sur Helply Swiss!'
                    : 'Willkommen bei Helply Swiss!'}
                </h3>
                <p className="text-sm text-green-800">
                  {language === 'en' 
                    ? 'The app where you can earn extra income and increase your professional potential.'
                    : language === 'it'
                    ? 'L\'app dove puoi guadagnare un reddito extra e aumentare il tuo potenziale professionale.'
                    : language === 'fr'
                    ? 'L\'application où vous pouvez gagner un revenu supplémentaire et augmenter votre potentiel professionnel.'
                    : 'Die App, mit der Sie zusätzliches Einkommen erzielen und Ihr berufliches Potenzial steigern können.'}
                </p>
              </div>
            </div>
          </Alert>
        )}

        <div className="mb-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold font-heading">{t.title}</h2>
          <Button 
            onClick={fetchAIMatches} 
            disabled={aiLoading}
            className="h-9 w-9 bg-blue-700 px-0 hover:bg-blue-800 sm:w-auto sm:px-3"
            data-testid="get-ai-matches-btn"
            aria-label={t.getAIMatches}
            title={t.getAIMatches}
          >
            <Sparkles className="w-5 h-5 sm:mr-2" />
            <span className="hidden sm:inline">{aiLoading ? '...' : t.getAIMatches}</span>
          </Button>
        </div>

        {/* 3-column layout: Categories | Jobs | Banners */}
        <div className="flex gap-6">

          {/* LEFT: Categories Sidebar */}
          <aside className="hidden sm:block w-44 flex-shrink-0" data-testid="pro-categories-sidebar">
            <div className="bg-white rounded-xl border border-gray-200 sticky top-20">
              <div className="p-3 border-b border-gray-100">
                <h2 className="text-sm font-bold text-gray-800 mb-2">
                  {language === 'en' ? 'Browse Categories' : language === 'de' ? 'Kategorien' : language === 'it' ? 'Categorie' : 'Catégories'}
                </h2>
                <input
                  type="text"
                  placeholder={language === 'en' ? 'Filter...' : language === 'de' ? 'Filtern...' : language === 'it' ? 'Filtra...' : 'Filtrer...'}
                  value={categorySearch}
                  onChange={e => setCategorySearch(e.target.value)}
                  className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div className="overflow-y-auto max-h-[calc(100vh-220px)]">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between hover:bg-blue-50 transition-colors ${!selectedCategory ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700'}`}
                  data-testid="pro-category-all"
                >
                  <span>{language === 'en' ? 'All Categories' : language === 'de' ? 'Alle Kategorien' : language === 'it' ? 'Tutte le categorie' : 'Toutes catégories'}</span>
                  {!selectedCategory && <ChevronRight className="w-3 h-3" />}
                </button>
                {SERVICE_CATEGORIES
                  .filter(cat => {
                    const name = language === 'en' ? cat.en : language === 'it' ? (cat.it || cat.en) : language === 'fr' ? (cat.fr || cat.en) : cat.de;
                    return name.toLowerCase().includes(categorySearch.toLowerCase());
                  })
                  .map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => setSelectedCategory(cat.value === selectedCategory ? null : cat.value)}
                      className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between hover:bg-blue-50 transition-colors border-t border-gray-50 ${selectedCategory === cat.value ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-600'}`}
                      data-testid={`pro-category-sidebar-${cat.value}`}
                    >
                      <span className="line-clamp-2 leading-tight">
                        {language === 'en' ? cat.en : language === 'it' ? (cat.it || cat.en) : language === 'fr' ? (cat.fr || cat.en) : cat.de}
                      </span>
                      {selectedCategory === cat.value && <ChevronRight className="w-3 h-3 flex-shrink-0" />}
                    </button>
                  ))}
              </div>
            </div>
          </aside>

          {/* CENTER: Tabs + Jobs */}
          <div className="flex-1 min-w-0">
            {/* Mobile categories horizontal scroll — only when sidebar is hidden */}
            <div className="sm:hidden mb-4 max-w-full">
              <div className="flex flex-wrap gap-2 pb-2">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`max-w-full px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${!selectedCategory ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200'}`}
                  data-testid="mobile-category-all"
                >
                  {language === 'en' ? 'All' : language === 'de' ? 'Alle' : language === 'it' ? 'Tutti' : 'Tous'}
                </button>
                {SERVICE_CATEGORIES.map(cat => (
                  <button
                    key={cat.value}
                    onClick={() => setSelectedCategory(cat.value === selectedCategory ? null : cat.value)}
                    className={`max-w-full whitespace-normal break-words px-3 py-1.5 rounded-full text-left text-xs font-medium border transition-colors ${selectedCategory === cat.value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200'}`}
                    data-testid={`mobile-category-${cat.value}`}
                  >
                    {language === 'en' ? cat.en : language === 'it' ? (cat.it || cat.en) : language === 'fr' ? (cat.fr || cat.en) : cat.de}
                  </button>
                ))}
              </div>
            </div>

        <Tabs defaultValue="all" data-testid="jobs-tabs">
          <TabsList className="mb-6 grid h-auto w-full grid-cols-3">
            <TabsTrigger value="myjobs" className="min-w-0 whitespace-normal px-1 text-xs sm:px-3 sm:text-sm" data-testid="my-jobs-tab">
              <Briefcase className="mr-1 hidden h-4 w-4 sm:block" />
              {t.myJobs} {myJobs.length > 0 && `(${myJobs.length})`}
            </TabsTrigger>
            <TabsTrigger value="all" className="min-w-0 whitespace-normal px-1 text-xs sm:px-3 sm:text-sm" data-testid="all-jobs-tab">
              <Briefcase className="mr-1 hidden h-4 w-4 sm:block" />
              {t.allJobs}
              {selectedCategory && <Badge className="ml-2 hidden text-xs bg-blue-100 text-blue-700 border-0 sm:inline-flex">{SERVICE_CATEGORIES.find(c => c.value === selectedCategory)?.en}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="ai" className="min-w-0 whitespace-normal px-1 text-xs sm:px-3 sm:text-sm" data-testid="ai-matches-tab">
              <Sparkles className="mr-1 hidden h-4 w-4 sm:block" />
              {t.aiMatches} {aiRecommendations.length > 0 && `(${aiRecommendations.length})`}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="myjobs">
            {myJobsLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
              </div>
            ) : myJobs.length === 0 ? (
              <Card className="p-12 text-center">
                <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 mb-2">{t.noMyJobs}</p>
                <p className="text-sm text-gray-400">{t.assignedJobs}</p>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {myJobs.map((job) => <MyJobCard key={job.id} job={job} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="all">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
              </div>
            ) : jobs.filter(j => !selectedCategory || j.category === selectedCategory).length === 0 ? (
              <Card className="p-12 text-center">
                <p className="text-gray-500">{t.noJobs}</p>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {jobs
                  .filter(j => !selectedCategory || j.category === selectedCategory)
                  .map((job) => <JobCard key={job.id} job={job} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="ai">
            {aiRecommendations.length === 0 ? (
              <Card className="p-12 text-center">
                <Sparkles className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">
                  {language === 'en' 
                    ? 'Click "Get AI Matches" to see personalized job recommendations'
                    : language === 'it'
                    ? 'Clicca "Ottieni Suggerimenti IA" per vedere i lavori consigliati'
                    : 'Klicken Sie auf "KI-Empfehlungen abrufen", um personalisierte Auftragsempfehlungen zu erhalten'}
                </p>
                <Button onClick={fetchAIMatches} disabled={aiLoading} data-testid="get-ai-matches-empty-btn">
                  {t.getAIMatches}
                </Button>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {aiRecommendations.map((job) => <JobCard key={job.id} job={job} isAIMatch />)}
              </div>
            )}
          </TabsContent>
        </Tabs>
          </div>

          {/* RIGHT: Banners Sidebar — desktop */}
          <aside className="hidden sm:block w-44 flex-shrink-0" data-testid="pro-banner-sidebar">
            <div className="sticky top-20">
              <BannerSidebar />
            </div>
          </aside>

        </div>

        {/* Mobile Banners — shown below jobs on phone only */}
        <BannerMobileStrip />
      </main>

      {/* Bid Dialog */}
      {bidDialogOpen && selectedJobForBid && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="bid-dialog-overlay">
          <div className="bg-white rounded-lg max-w-md w-full p-6 relative" role="dialog" aria-modal="true" data-testid="bid-dialog">
            <button
              onClick={closeBidDialog}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              data-testid="bid-dialog-close-button"
              aria-label="Close bid dialog"
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className="text-xl font-bold mb-4">
              {language === 'en' ? 'Place Your Bid' : language === 'it' ? 'Fai la Tua Offerta' : 'Ihr Angebot abgeben'}
            </h3>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                {language === 'en' ? 'Job:' : language === 'it' ? 'Lavoro:' : 'Auftrag:'}
              </p>
              <p className="font-medium">{selectedJobForBid.title}</p>
              <p className="text-sm text-gray-500">
                {language === 'en' ? 'Budget:' : 'Budget:'} CHF {selectedJobForBid.budget}
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="bid-amount">
                  {language === 'en' ? 'Your Bid Amount (CHF) *' : language === 'it' ? 'Importo Offerta (CHF) *' : 'Ihr Angebotsbetrag (CHF) *'}
                </Label>
                <Input
                  id="bid-amount"
                  type="number"
                  value={bidAmount}
                  onChange={(e) => setBidAmount(e.target.value)}
                  placeholder="1500"
                  min="0"
                  step="0.01"
                  data-testid="bid-price-input"
                />
              </div>

              <div>
                <Label htmlFor="bid-message">
                  {language === 'en' ? 'Message (Optional)' : language === 'it' ? 'Messaggio (Opzionale)' : 'Nachricht (Optional)'}
                </Label>
                <Textarea
                  id="bid-message"
                  value={bidMessage}
                  onChange={(e) => setBidMessage(e.target.value)}
                  placeholder={language === 'en' ? 'Tell the client why you\'re the best choice...' : language === 'it' ? 'Spiega al cliente perché sei la scelta migliore...' : 'Sagen Sie dem Kunden, warum Sie die beste Wahl sind...'}
                  rows={3}
                  data-testid="bid-message-input"
                />
              </div>

              <div className="flex gap-2 mt-6">
                <Button
                  variant="outline"
                  onClick={closeBidDialog}
                  className="flex-1"
                  disabled={submittingBid}
                  data-testid="bid-cancel-button"
                >
                  {language === 'en' ? 'Cancel' : language === 'it' ? 'Annulla' : 'Abbrechen'}
                </Button>
                <Button
                  onClick={placeBid}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={submittingBid || !bidAmount}
                  data-testid="bid-submit-button"
                >
                  {submittingBid ? '...' : (language === 'en' ? 'Submit Bid' : language === 'it' ? 'Invia Offerta' : 'Angebot abgeben')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessionalDashboard;